import React from 'react';
import { formatMoney, useCurrency } from '../context/CurrencyContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { itemLabel } from '../lib/activityItems';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { 
  Calendar, Users, Check, ChevronRight, 
  ChevronLeft, Loader2, Plus, Trash2, CreditCard, AlertCircle, MapPin, BanIcon
} from 'lucide-react';
import { toast } from 'sonner';

var API_URL = process.env.REACT_APP_BACKEND_URL;

function formatYen(amount, cur) {
  return formatMoney(amount, cur);
}

function formatDateFr(s) {
  if (!s) return 'Choisir une date';
  var d = new Date(s + 'T00:00:00');
  var txt = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return txt.charAt(0).toUpperCase() + txt.slice(1);
}

var WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function dateToStr(d) {
  var y = d.getFullYear();
  var m = ('0' + (d.getMonth() + 1)).slice(-2);
  var day = ('0' + d.getDate()).slice(-2);
  return y + '-' + m + '-' + day;
}

var idCounter = 0;
function makeId() {
  idCounter = idCounter + 1;
  return 'pid_' + Date.now() + '_' + idCounter;
}

function ParticipantRow(props) {
  var p = props.participant;
  var idx = props.index;
  var lockAdult = props.lockAdult;
  return (
    <div className="bg-zinc-800/50 rounded-xl p-4 border border-white/10">
      <div className="flex justify-between mb-3">
        <span className="text-zinc-300">
          Participant {idx + 1}
          {lockAdult && <span className="text-xs text-[#00F0FF] ml-2">(vous)</span>}
        </span>
        {props.canRemove && (
          <button onClick={props.onRemove} className="text-red-400 hover:text-red-300" type="button" data-testid={'participant-remove-' + idx}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400 text-sm mb-1 block">Nom</Label>
          <Input
            value={p.name}
            onChange={function(e) { props.onUpdate('name', e.target.value); }}
            placeholder="Nom complet"
            className="bg-black/20 border-white/10 text-white"
          />
        </div>
        <div>
          <Label className="text-zinc-400 text-sm mb-1 block">Tarif</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={function() { props.onUpdate('rate_type', 'adult'); }}
              className={'flex-1 ' + (p.rate_type === 'adult' ? 'bg-[#00F0FF] text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700')}
            >
              Adulte
            </Button>
            {lockAdult ? (
              <Button type="button" disabled className="flex-1 bg-zinc-900 text-zinc-600 cursor-not-allowed">
                Adulte requis
              </Button>
            ) : props.childrenAllowed ? (
              <Button
                type="button"
                onClick={function() { props.onUpdate('rate_type', 'child'); }}
                className={'flex-1 ' + (p.rate_type === 'child' ? 'bg-[#D4AF37] text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700')}
              >
                Enfant
              </Button>
            ) : (
              <Button type="button" disabled className="flex-1 bg-zinc-900 text-zinc-600 cursor-not-allowed">
                <BanIcon className="h-4 w-4 mr-1" /> Interdit
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 text-right text-zinc-400 text-sm">
        {formatYen(p.rate_type === 'adult' ? props.adultRate : props.childRate)}/jour
      </div>
    </div>
  );
}

export default function BookingActivity() {
  useCurrency(); // re-render quand la devise d'affichage change
  var { t } = useTranslation();
  var params = useParams();
  var activityId = params.activityId;
  var navigate = useNavigate();
  var auth = useAuth();
  var currentUser = auth.user;

  var activityState = React.useState(null);
  var activity = activityState[0];
  var setActivity = activityState[1];

  var availState = React.useState(null); // { days_of_week, start_date, end_date, blocked_dates }
  var avail = availState[0];
  var setAvail = availState[1];

  function isDateDisabled(date) {
    var today = new Date(); today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    if (!avail) return false;
    var ds = dateToStr(date);
    if (avail.start_date && ds < avail.start_date) return true;
    if (avail.end_date && ds > avail.end_date) return true;
    var days = avail.days_of_week || [];
    if (days.length > 0 && days.indexOf(WEEKDAYS[date.getDay()]) < 0) return true; // jour non proposé
    if ((avail.blocked_dates || []).indexOf(ds) >= 0) return true;                 // bloqué / déjà réservé
    return false;
  }

  // Nombre max de jours CONSÉCUTIFS disponibles à partir d'une date de départ.
  function maxDaysFromStart(startStr) {
    if (!startStr) return 7;
    var base = new Date(startStr + 'T00:00:00');
    var max = 0;
    for (var i = 0; i < 7; i++) {
      var d = new Date(base); d.setDate(base.getDate() + i);
      if (isDateDisabled(d)) break;
      max = i + 1;
    }
    return max || 1;
  }

  var loadingState = React.useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var submittingState = React.useState(false);
  var submitting = submittingState[0];
  var setSubmitting = submittingState[1];

  var stepState = React.useState(1);
  var currentStep = stepState[0];
  var setCurrentStep = stepState[1];

  var dateState = React.useState('');
  var startDate = dateState[0];
  var setStartDate = dateState[1];

  var daysState = React.useState(1);
  var numberOfDays = daysState[0];
  var setNumberOfDays = daysState[1];

  var participantsState = React.useState([{ id: makeId(), name: '', rate_type: 'adult' }]);
  var participants = participantsState[0];
  var setParticipants = participantsState[1];

  var priceState = React.useState(null);
  var priceDetails = priceState[0];
  var setPriceDetails = priceState[1];

  var promoInputState = React.useState('');
  var promoInput = promoInputState[0];
  var setPromoInput = promoInputState[1];
  var appliedPromoState = React.useState(null); // { code, discount_percent }
  var appliedPromo = appliedPromoState[0];
  var setAppliedPromo = appliedPromoState[1];
  var promoMsgState = React.useState('');
  var promoMsg = promoMsgState[0];
  var setPromoMsg = promoMsgState[1];
  // Cagnotte du voyageur (cadeau de la plateforme, en EUR côté serveur).
  var walletState = React.useState(null);      // { balance_eur, balance_local }
  var wallet = walletState[0];
  var setWallet = walletState[1];
  var useWalletState = React.useState(true);   // proposée activée : c'est un cadeau
  var useWallet = useWalletState[0];
  var setUseWallet = useWalletState[1];

  React.useEffect(function() {
    axios.get(API_URL + '/api/wallet/me?currency=jpy', { withCredentials: true })
      .then(function(r) { setWallet(r.data); })
      .catch(function() { setWallet(null); });   // non connecté ou pas de cagnotte
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyPromo() {
    var code = (promoInput || '').trim();
    if (!code) return;
    axios.post(API_URL + '/api/promo/validate', { code: code }, { withCredentials: true })
      .then(function(res) {
        if (res.data.valid) {
          setAppliedPromo({ code: res.data.code, discount_percent: res.data.discount_percent });
          setPromoMsg('');
          toast.success('Code appliqué : -' + res.data.discount_percent + '%');
        } else {
          setAppliedPromo(null);
          setPromoMsg(res.data.message || 'Code invalide');
        }
      })
      .catch(function() { setAppliedPromo(null); setPromoMsg('Erreur de vérification'); });
  }
  function removePromo() { setAppliedPromo(null); setPromoInput(''); setPromoMsg(''); }
  function currentDiscount() {
    if (!appliedPromo || !priceDetails) return 0;
    return Math.round(priceDetails.total_price * appliedPromo.discount_percent / 100);
  }
  // Reste à payer avant cagnotte.
  function afterDiscount() {
    return Math.max((priceDetails ? priceDetails.total_price : 0) - currentDiscount(), 0);
  }
  // Cagnotte réellement mobilisable sur cette réservation. Le serveur refait ce
  // calcul et reste seul juge : ceci n'est qu'un aperçu.
  function walletApplied() {
    if (!useWallet || !wallet || !priceDetails) return 0;
    var dispo = wallet.balance_local || 0;
    return Math.min(dispo, afterDiscount());
  }
  function currentTotal() {
    return Math.max(afterDiscount() - walletApplied(), 0);
  }

  var calendarOpenState = React.useState(false);
  var calendarOpen = calendarOpenState[0];
  var setCalendarOpen = calendarOpenState[1];

  var mountedRef = React.useRef(true);
  var redirectingRef = React.useRef(false);
  var prefilledRef = React.useRef(false);

  React.useEffect(function() {
    mountedRef.current = true;
    return function() { mountedRef.current = false; };
  }, []);

  // Auto-fill participant 1 with the logged-in client's name (adult, non-removable)
  React.useEffect(function() {
    if (currentUser && currentUser.name && !prefilledRef.current) {
      prefilledRef.current = true;
      setParticipants(function(prev) {
        var list = prev.slice();
        if (list.length > 0 && !list[0].name) {
          list[0] = { id: list[0].id, name: currentUser.name, rate_type: 'adult' };
        }
        return list;
      });
    }
  }, [currentUser]);

  React.useEffect(function() {
    var cancelled = false;
    axios.get(API_URL + '/api/activities/' + activityId + '/booking-availability')
      .then(function(r) { if (!cancelled) setAvail(r.data); })
      .catch(function() {});
    axios.get(API_URL + '/api/activities/' + activityId)
      .then(function(res) {
        if (!cancelled) {
          setActivity(res.data);
          setLoading(false);
        }
      })
      .catch(function() {
        if (!cancelled) {
          toast.error('Activité non trouvée');
          navigate('/activities');
        }
      });
    return function() { cancelled = true; };
  }, [activityId, navigate]);

  React.useEffect(function() {
    if (!activity || !startDate || numberOfDays < 1 || participants.length === 0) return;

    var calcList = [];
    var i;
    for (i = 0; i < participants.length; i++) {
      calcList.push({
        name: participants[i].name.trim() || ('Participant ' + (i + 1)),
        rate_type: participants[i].rate_type
      });
    }

    var cancelled = false;
    axios.post(API_URL + '/api/bookings/calculate', {
      activity_id: activityId,
      start_date: startDate,
      number_of_days: numberOfDays,
      participants: calcList
    }, { withCredentials: true })
      .then(function(res) {
        if (!cancelled && mountedRef.current) {
          setPriceDetails(res.data);
        }
      })
      .catch(function(err) {
        if (!cancelled && mountedRef.current && err.response && err.response.data && err.response.data.detail) {
          toast.error(err.response.data.detail);
        }
      });

    return function() { cancelled = true; };
  }, [activityId, startDate, numberOfDays, participants, activity, currentStep]);

  function addParticipant() {
    var max = activity ? activity.max_participants : 4;
    if (participants.length >= max) {
      toast.error('Maximum ' + max + ' participants');
      return;
    }
    var newList = participants.slice();
    newList.push({ id: makeId(), name: '', rate_type: 'adult' });
    setParticipants(newList);
  }

  function removeParticipant(removeId) {
    var newList = [];
    var i;
    for (i = 0; i < participants.length; i++) {
      if (participants[i].id !== removeId) {
        newList.push(participants[i]);
      }
    }
    setParticipants(newList);
  }

  function updateParticipant(updateId, field, value) {
    var newList = [];
    var i;
    for (i = 0; i < participants.length; i++) {
      if (participants[i].id === updateId) {
        var updated = { id: participants[i].id, name: participants[i].name, rate_type: participants[i].rate_type };
        updated[field] = value;
        newList.push(updated);
      } else {
        newList.push(participants[i]);
      }
    }
    setParticipants(newList);
  }

  function handleSubmit() {
    if (submitting || redirectingRef.current) return;

    if (!startDate) { toast.error('Choisissez une date de départ'); return; }
    // Tous les participants doivent avoir un nom : sinon le prix affiché
    // (qui les compte tous) diffère du prix payé (qui ne garderait que les nommés).
    var allNamed = participants.length > 0 && participants.every(function(p) { return p.name.trim(); });
    if (!allNamed) {
      toast.error('Renseignez le nom de chaque participant');
      return;
    }
    var finalP = participants.map(function(p) { return { name: p.name.trim(), rate_type: p.rate_type }; });

    setSubmitting(true);
    axios.post(API_URL + '/api/bookings/create', {
      activity_id: activityId,
      start_date: startDate,
      number_of_days: numberOfDays,
      participants: finalP,
      promo_code: appliedPromo ? appliedPromo.code : null,
      use_wallet: !!useWallet,
      origin_url: window.location.origin
    }, { withCredentials: true })
      .then(function(res) {
        if (res.data.checkout_url) {
          redirectingRef.current = true;
          window.location.replace(res.data.checkout_url);
        }
      })
      .catch(function(err) {
        if (redirectingRef.current || !mountedRef.current) return;
        var msg = 'Erreur';
        if (err.response && err.response.data && err.response.data.detail) {
          msg = err.response.data.detail;
        }
        toast.error(msg);
        setSubmitting(false);
      });
  }

  var priceLoadingState = React.useState(false);
  var priceLoading = priceLoadingState[0];
  var setPriceLoading = priceLoadingState[1];

  function goNext() {
    if (currentStep === 2) {
      var i;
      // Tous les participants doivent être nommés pour que le récap = le prix payé.
      var allNamed = participants.length > 0 && participants.every(function(p) { return p.name.trim(); });
      if (!allNamed) {
        toast.error('Renseignez le nom de chaque participant');
        return;
      }

      // Force price calculation before going to step 3
      setPriceLoading(true);
      var calcList = [];
      for (i = 0; i < participants.length; i++) {
        calcList.push({
          name: participants[i].name.trim() || ('Participant ' + (i + 1)),
          rate_type: participants[i].rate_type
        });
      }
      
      axios.post(API_URL + '/api/bookings/calculate', {
        activity_id: activityId,
        start_date: startDate,
        number_of_days: numberOfDays,
        participants: calcList
      }, { withCredentials: true })
        .then(function(res) {
          if (mountedRef.current) {
            setPriceDetails(res.data);
            setPriceLoading(false);
            setCurrentStep(3);
          }
        })
        .catch(function(err) {
          setPriceLoading(false);
          if (err.response && err.response.status < 500 && err.response.data && err.response.data.detail) {
            // Erreur de validation du serveur (activité non dispo, enfants interdits…)
            toast.error(err.response.data.detail);
          } else {
            // Erreur réseau ou serveur : on laisse passer et on réessaie au paiement
            toast.error('Calcul du prix temporairement indisponible');
            if (mountedRef.current) setCurrentStep(3);
          }
        });
      return;
    }
    setCurrentStep(currentStep + 1);
  }

  function goBack() {
    setCurrentStep(currentStep - 1);
  }

  var today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-20">
        <Loader2 className="h-12 w-12 text-[#F9004D] animate-spin" />
      </div>
    );
  }

  var adultRate = activity ? activity.adult_rate : 38000;
  var childRate = activity ? activity.child_rate : 20000;
  var childrenAllowed = activity ? activity.children_allowed !== false : true;
  var maxParticipants = activity ? activity.max_participants : 4;
  var actName = activity && activity.name ? (activity.name.fr || activity.name.en) : 'Activité';
  var actLoc = activity ? activity.location : '';
  var guideName = activity ? activity.guide_name : '';

  // Build participant rows with stable IDs
  var participantRows = [];
  var k;
  for (k = 0; k < participants.length; k++) {
    var part = participants[k];
    participantRows.push(
      <ParticipantRow
        key={part.id}
        participant={part}
        index={k}
        onUpdate={function(pid) { return function(f, v) { updateParticipant(pid, f, v); }; }(part.id)}
        onRemove={function(pid) { return function() { removeParticipant(pid); }; }(part.id)}
        canRemove={participants.length > 1 && k > 0}
        lockAdult={k === 0}
        adultRate={adultRate}
        childRate={childRate}
        childrenAllowed={childrenAllowed}
      />
    );
  }

  // Build summary participants
  var summaryRows = [];
  if (priceDetails && priceDetails.participants) {
    var m;
    for (m = 0; m < priceDetails.participants.length; m++) {
      var pp = priceDetails.participants[m];
      summaryRows.push(
        <div key={'sum_' + m} className="flex justify-between text-white">
          <span>
            {pp.name}{' '}
            <span className={'text-xs ' + (pp.rate_type === 'adult' ? 'text-[#00F0FF]' : 'text-[#D4AF37]')}>
              ({pp.rate_type === 'adult' ? 'Adulte' : 'Enfant'})
            </span>
          </span>
          <span>{formatYen(pp.rate_amount)}/jour</span>
        </div>
      );
    }
  }

  // Build included items
  var includedRows = [];
  if (activity && activity.included) {
    var n;
    for (n = 0; n < activity.included.length; n++) {
      includedRows.push(
        <p key={'inc_' + n} className="text-green-400 text-sm">
          <Check className="inline h-3 w-3 mr-1" />{itemLabel(t, activity.included[n])}
        </p>
      );
    }
  }

  var maxDays = maxDaysFromStart(startDate);

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Progress */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center">
            <div className={'w-10 h-10 rounded-full flex items-center justify-center font-bold ' + (currentStep >= 1 ? 'bg-[#F9004D] text-white' : 'bg-zinc-800 text-zinc-500')}>
              {currentStep > 1 ? <Check className="h-5 w-5" /> : '1'}
            </div>
            <div className={'w-16 h-1 mx-2 ' + (currentStep > 1 ? 'bg-[#F9004D]' : 'bg-zinc-800')} />
          </div>
          <div className="flex items-center">
            <div className={'w-10 h-10 rounded-full flex items-center justify-center font-bold ' + (currentStep >= 2 ? 'bg-[#F9004D] text-white' : 'bg-zinc-800 text-zinc-500')}>
              {currentStep > 2 ? <Check className="h-5 w-5" /> : '2'}
            </div>
            <div className={'w-16 h-1 mx-2 ' + (currentStep > 2 ? 'bg-[#F9004D]' : 'bg-zinc-800')} />
          </div>
          <div className="flex items-center">
            <div className={'w-10 h-10 rounded-full flex items-center justify-center font-bold ' + (currentStep >= 3 ? 'bg-[#F9004D] text-white' : 'bg-zinc-800 text-zinc-500')}>
              3
            </div>
          </div>
        </div>

        {/* Activity Header */}
        <Card className="bg-zinc-900/50 border-white/10 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-[#F9004D]/20 flex items-center justify-center">
              <MapPin className="h-8 w-8 text-[#F9004D]" />
            </div>
            <div className="flex-1">
              <span className="text-[#F9004D] text-sm font-bold">{actLoc}</span>
              <h2 className="text-xl font-bold text-white">{actName}</h2>
              <p className="text-zinc-400 text-sm">avec {guideName}</p>
            </div>
            {!childrenAllowed && (
              <div className="px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
                <span className="text-red-400 text-sm flex items-center gap-1">
                  <BanIcon className="h-4 w-4" /> -18 ans
                </span>
              </div>
            )}
          </div>
        </Card>

        {/* Step 1: Date */}
        {currentStep === 1 && (
          <Card className="bg-zinc-900/50 border-white/10 p-6">
            <h3 className="text-xl font-bold text-white mb-6">
              <Calendar className="inline h-5 w-5 mr-2 text-[#F9004D]" />
              Date et durée
            </h3>
            <div className="space-y-6">
              <div>
                <Label className="text-zinc-300 mb-2 block">Date de départ</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="date-field-banner"
                      className={'w-full flex items-center gap-3 bg-black/20 border border-white/10 rounded-md py-5 px-5 text-left hover:border-[#F9004D]/60 transition-colors ' + (startDate ? 'text-white' : 'text-zinc-400')}
                    >
                      <Calendar className="h-7 w-7 text-[#F9004D] shrink-0" />
                      <span className="text-lg">{formatDateFr(startDate)}</span>
                      <ChevronRight className="h-5 w-5 ml-auto text-zinc-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-auto p-0 bg-zinc-900 border-white/10 text-white"
                    data-testid="date-calendar-popover"
                  >
                    <CalendarPicker
                      mode="single"
                      selected={startDate ? new Date(startDate + 'T00:00:00') : undefined}
                      onSelect={function(d) {
                        if (d) {
                          var s = dateToStr(d);
                          setStartDate(s);
                          var md = maxDaysFromStart(s);
                          setNumberOfDays(function(prev) { return Math.min(prev, md); });
                          setCalendarOpen(false);
                        }
                      }}
                      disabled={isDateDisabled}
                      initialFocus
                      classNames={{
                        months: 'flex flex-col',
                        month: 'space-y-4 p-2',
                        caption: 'flex justify-center pt-1 relative items-center mb-2',
                        caption_label: 'text-lg font-bold text-white',
                        nav_button: 'h-9 w-9 bg-zinc-800 hover:bg-zinc-700 rounded-md text-white flex items-center justify-center',
                        nav_button_previous: 'absolute left-1',
                        nav_button_next: 'absolute right-1',
                        head_cell: 'text-zinc-400 w-12 font-normal text-sm',
                        cell: 'relative p-0 text-center',
                        day: 'h-12 w-12 p-0 font-normal text-base rounded-md text-zinc-200 hover:bg-zinc-700 aria-selected:opacity-100',
                        day_selected: 'bg-[#F9004D] text-white hover:bg-[#F9004D] focus:bg-[#F9004D]',
                        day_today: 'bg-zinc-800 text-[#00F0FF] font-bold',
                        day_disabled: 'text-zinc-600 opacity-40 cursor-not-allowed'
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-zinc-300 mb-2 block">Nombre de jours</Label>
                <div className="flex items-center gap-4">
                  <Button variant="outline" onClick={function() { setNumberOfDays(Math.max(1, numberOfDays - 1)); }} className="h-14 w-14 border-white/20 text-white text-xl">-</Button>
                  <div className="text-5xl font-bold text-white w-24 text-center" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{numberOfDays}</div>
                  <Button variant="outline" disabled={numberOfDays >= maxDays} onClick={function() { setNumberOfDays(Math.min(maxDays, numberOfDays + 1)); }} className="h-14 w-14 border-white/20 text-white text-xl disabled:opacity-40">+</Button>
                  <span className="text-zinc-400 text-lg">jour{numberOfDays > 1 ? 's' : ''}</span>
                </div>
                {maxDays < 7 && (
                  <p className="text-zinc-500 text-xs mt-2">Max {maxDays} jour{maxDays > 1 ? 's' : ''} consécutif{maxDays > 1 ? 's' : ''} disponible{maxDays > 1 ? 's' : ''} à partir de cette date.</p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Step 2: Participants */}
        {currentStep === 2 && (
          <Card className="bg-zinc-900/50 border-white/10 p-6">
            <h3 className="text-xl font-bold text-white mb-6">
              <Users className="inline h-5 w-5 mr-2 text-[#F9004D]" />
              Participants
            </h3>
            <div className="space-y-4">
              {participantRows}
              {priceDetails && priceDetails.group_discount_amount > 0 && (
                <div className="flex items-center justify-between gap-2 text-green-400 bg-green-400/10 border border-green-400/20 rounded-lg p-3 text-sm" data-testid="group-discount-banner">
                  <span className="flex items-center gap-2"><Check className="h-4 w-4" /> Réduction groupe {priceDetails.group_discount_percent}% appliquée</span>
                  <span className="font-bold">−{formatYen(priceDetails.group_discount_amount)}</span>
                </div>
              )}
              {participants.length < maxParticipants && (
                <Button variant="outline" onClick={addParticipant} className="w-full border-dashed border-white/20 text-zinc-300 hover:bg-white/5">
                  <Plus className="h-4 w-4 mr-2" /> Ajouter un participant
                </Button>
              )}
              {participants.length >= maxParticipants && (
                <div className="flex items-center gap-2 text-amber-400 bg-amber-400/10 rounded-lg p-3 text-sm">
                  <AlertCircle className="h-4 w-4" /> Maximum {maxParticipants} participants
                </div>
              )}
              {!childrenAllowed && (
                <div className="flex items-center gap-2 text-red-400 bg-red-400/10 rounded-lg p-3 text-sm">
                  <BanIcon className="h-4 w-4" /> Les enfants ne sont pas autorisés
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Step 3: Summary */}
        {currentStep === 3 && (
          <Card className="bg-zinc-900/50 border-white/10 p-6">
            <h3 className="text-xl font-bold text-white mb-6">
              <CreditCard className="inline h-5 w-5 mr-2 text-[#F9004D]" />
              Récapitulatif
            </h3>
            <div className="space-y-4">
              <div className="border-b border-white/10 pb-4">
                <p className="text-zinc-400 text-sm">ACTIVITÉ</p>
                <p className="text-white text-lg font-bold">{actName}</p>
                <p className="text-[#F9004D]">{actLoc}</p>
                <p className="text-zinc-400 text-sm">avec {guideName}</p>
              </div>
              <div className="border-b border-white/10 pb-4">
                <p className="text-zinc-400 text-sm">DATE</p>
                <p className="text-white">{startDate && new Date(startDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                <p className="text-[#00F0FF] font-bold">{numberOfDays} jour{numberOfDays > 1 ? 's' : ''}</p>
              </div>
              <div className="border-b border-white/10 pb-4">
                <p className="text-zinc-400 text-sm">PARTICIPANTS ({priceDetails ? priceDetails.participants.length : 0})</p>
                {summaryRows}
              </div>
              {includedRows.length > 0 && (
                <div className="border-b border-white/10 pb-4">
                  <p className="text-zinc-400 text-sm">INCLUS</p>
                  {includedRows}
                </div>
              )}
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <div className="flex justify-between text-zinc-300 mb-2">
                  <span>Sous-total / jour</span>
                  <span>{formatYen(priceDetails ? priceDetails.subtotal_per_day : 0)}</span>
                </div>
                <div className="flex justify-between text-zinc-300 mb-2">
                  <span>× {numberOfDays} jour{numberOfDays > 1 ? 's' : ''}</span>
                  <span>{formatYen(priceDetails ? (priceDetails.gross_base || priceDetails.base_amount || 0) : 0)}</span>
                </div>
                {priceDetails && priceDetails.group_discount_amount > 0 && (
                  <div className="flex justify-between text-green-400 text-sm mb-2">
                    <span>Réduction groupe (−{priceDetails.group_discount_percent}%)</span>
                    <span>−{formatYen(priceDetails.group_discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-zinc-400 text-sm mb-4">
                  <span>Frais de traitement</span>
                  <span>{formatYen(priceDetails ? (priceDetails.service_fee || 0) : 0)}</span>
                </div>

                {/* Code promo */}
                <div className="mb-4">
                  {appliedPromo ? (
                    <div className="flex justify-between items-center text-[#10B981] text-sm">
                      <span>Code {appliedPromo.code} (−{appliedPromo.discount_percent}%)</span>
                      <div className="flex items-center gap-3">
                        <span>−{formatYen(currentDiscount())}</span>
                        <button type="button" onClick={removePromo} className="text-zinc-500 hover:text-white underline text-xs">retirer</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex gap-2">
                        <Input value={promoInput} onChange={function(e) { setPromoInput(e.target.value.toUpperCase()); }}
                          onKeyDown={function(e) { if (e.key === 'Enter') applyPromo(); }}
                          placeholder="Code promo" className="bg-black/20 border-white/10 text-white flex-1" />
                        <Button type="button" onClick={applyPromo} variant="outline" className="border-white/20 text-zinc-300 hover:text-white">
                          Appliquer
                        </Button>
                      </div>
                      {promoMsg && <p className="text-red-400 text-xs mt-1">{promoMsg}</p>}
                    </div>
                  )}
                </div>

                {/* Cagnotte : cadeau de la plateforme, déduit du montant à payer */}
                {wallet && (wallet.balance_local || 0) > 0 && (
                  <div className="mb-4 rounded-lg border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!useWallet}
                        onChange={function(e) { setUseWallet(e.target.checked); }}
                        className="mt-1 accent-[#00F0FF]"
                        data-testid="use-wallet-checkbox"
                      />
                      <span className="text-sm">
                        <span className="text-white font-semibold">Utiliser ma cagnotte</span>
                        <span className="text-zinc-400"> — solde {formatYen(wallet.balance_local)}</span>
                        {useWallet && walletApplied() > 0 && (
                          <span className="block text-[#00F0FF] mt-1">
                            −{formatYen(walletApplied())} déduits de cette réservation
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                )}

                <div className="border-t border-white/10 pt-4 flex justify-between items-center">
                  <span className="text-xl font-bold text-white">TOTAL</span>
                  <span className="text-3xl font-bold text-[#F9004D]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatYen(currentTotal())}
                  </span>
                </div>
              </div>
              <div className="text-xs text-zinc-500 space-y-1">
                <p>• Paiement sécurisé par Stripe</p>
                <p>• Les frais de traitement couvrent les frais bancaires</p>
                <p>• Réservation confirmée après paiement</p>
              </div>
            </div>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          {currentStep > 1 ? (
            <Button onClick={goBack} variant="outline" className="border-white/20 text-white hover:bg-white/10" disabled={priceLoading}>
              <ChevronLeft className="h-4 w-4 mr-2" /> Retour
            </Button>
          ) : (
            <Button onClick={function() { navigate('/activities/' + activityId); }} variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <ChevronLeft className="h-4 w-4 mr-2" /> Annuler
            </Button>
          )}

          {currentStep < 3 ? (
            <Button
              onClick={goNext}
              disabled={(currentStep === 1 && !startDate) || priceLoading}
              className="bg-[#F9004D] hover:bg-[#D00040] text-white disabled:opacity-50"
            >
              {priceLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {priceLoading ? 'Calcul...' : 'Continuer'} {!priceLoading && <ChevronRight className="h-4 w-4 ml-2" />}
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#F9004D] hover:bg-[#D00040] text-white px-8">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
              Payer {formatYen(currentTotal())}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
